import React from 'react';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description }) => {
  return (
    <div className="bg-dark-light rounded-2xl p-8 flex flex-col">
      <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mb-6">
        {icon}
      </div>
      <h3 className="text-2xl font-bold text-white mb-4">{title}</h3>
      <p className="text-gray-400 text-lg">{description}</p>
    </div>
  );
};

// Icons
const RevenueIcon = () => (
  <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const SatisfactionIcon = () => (
  <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
  </svg>
);

const RiskIcon = () => (
  <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const VelocityIcon = () => (
  <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const ModelFeatures: React.FC = () => {
  return (
    <section className="bg-dark py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-300 mb-6">
             Why Choose ValetPe?
          </h2>
          <p className="text-xl text-gray-400 mb-8 max-w-3xl mx-auto">
            ValetPe accelerates the refund process, giving customers the confidence and flexibility to find something else they love, faster.
          </p>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            For retailers, this can mean increased customer satisfaction, loyalty and a quicker recapture of sales. ValetPe helps you turn refunds into revenue.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          <FeatureCard 
            icon={<RevenueIcon />}
            title="Recapture revenue and shoppers"
            description="75% of shoppers say they're more likely to make repeat purchases from a brand that offers instant refunds. ValetPe helps you capitalize on this insight; restoring your customer's purchasing power faster, and enabling them to repurchase with you."
          />
          
          <FeatureCard 
            icon={<SatisfactionIcon />}
            title="Improve customer satisfaction"
            description="With ValetPe, your customers can enjoy faster refunds – alleviating a common pain point in the shopping experience. This can enhance overall satisfaction, fostering long-term loyalty and confidence in purchasing at the point of sale."
          />
          
          <FeatureCard 
            icon={<RiskIcon />}
            title="Let us handle the risk"
            description="Faster refunds take trust. ValetPe handles the risks of unreturned item(s) or unsatisfactory return conditions - so you can focus on offering your customer a better return experience."
          />
          
          <FeatureCard 
            icon={<VelocityIcon />}
            title="Increase repurchase velocity"
            description="ValetPe minimizes missed buying opportunities linked to idle funds in the traditional returns process. By providing faster refunds, we restore your customer's purchasing power quickly. And by helping them rediscover your brand and items, we enable them to get what they want instead of waiting on their money."
          />
        </div>
      </div>
    </section>
  );
};

export default ModelFeatures;